import { useEffect, useRef, useState } from 'react';
import { clickBus } from '@/lib/clickBus';

export const ROUND = 10;
const HOLD_MS = 600;
const SETTLE_MS = 150;
const NEXT_MS = 900;
const MIN_TIMED_SAMPLES = 3;

export const scoreOf = (cents) => Math.max(0, Math.min(100, Math.round(100 - 2 * (Math.abs(cents) - 3))));
const median = (arr) => { const s = [...arr].sort((a, b) => a - b); return s[Math.floor(s.length / 2)]; };
export const matches = (interval, t) => {
  if (!interval || Math.abs(interval.cents) > 50) return false;
  const semis = interval.semis * t.dir;
  return semis > 0 && semis % 12 === t.s % 12;
};

const HISTORY_KEY = 'tuner.intervalHistory';
const HISTORY_LEN = 5;
const loadHistory = () => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '{}'); } catch { return {}; } };
const keyOf = (t) => `${t.dir > 0 ? 'u' : 'd'}${t.s}`;
export const avgOf = (arr) => (arr?.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null);
// Lower recent scores -> picked more often; unseen intervals get a medium weight.
const weightOf = (hist, t) => { const a = avgOf(hist[keyOf(t)]); return a === null ? 2.5 : 1 + (100 - a) / 25; };
const weightedPick = (pool, hist) => {
  const weights = pool.map((t) => weightOf(hist, t));
  let r = Math.random() * weights.reduce((a, b) => a + b, 0);
  for (let i = 0; i < pool.length; i++) { r -= weights[i]; if (r <= 0) return pool[i]; }
  return pool[pool.length - 1];
};

// Round of 10 intervals; free mode scores on hold, timed mode scores each click bar.
export function useIntervalRound({ interval, selected, direction, timed, barsPer = 1, allowed = () => true }) {
  const [round, setRound] = useState(null); // { targets, idx, results, waiting, countdown, bar }
  const [history, setHistory] = useState(loadHistory);
  const hold = useRef({ since: 0, samples: [] });
  const roundRef = useRef(round);
  roundRef.current = round;
  const intervalRef = useRef(interval);
  intervalRef.current = interval;

  const dirs = direction === 'down' ? [-1] : direction === 'up' ? [1] : [1, -1];
  const pool = dirs.flatMap((dir) => selected.filter((s) => allowed(s, dir)).map((s) => ({ s, dir })));

  const start = () => {
    if (!pool.length) return;
    const targets = Array.from({ length: ROUND }, () => weightedPick(pool, history));
    hold.current = { since: 0, samples: [] };
    if (timed) clickBus.control?.start();
    setRound({ targets, idx: 0, results: [], waiting: false, countdown: timed ? 2 : 0, bar: 0 });
  };
  const stop = () => setRound(null);

  const record = (score) => {
    hold.current = { since: 0, samples: [] };
    const cur = roundRef.current;
    const t = cur?.targets[cur.idx];
    if (t) {
      setHistory((h) => {
        const next = { ...h, [keyOf(t)]: [...(h[keyOf(t)] || []), score].slice(-HISTORY_LEN) };
        localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
        return next;
      });
    }
    if (timed) { setRound((r) => ({ ...r, results: [...r.results, score], idx: r.idx + 1, bar: 0 })); return; }
    setRound((r) => ({ ...r, results: [...r.results, score], waiting: true }));
    setTimeout(() => setRound((r) => (r ? { ...r, idx: r.idx + 1, waiting: false } : r)), NEXT_MS);
  };

  const playing = !!round && round.idx < ROUND;

  // Sample the detected interval against the current target.
  useEffect(() => {
    if (!playing || round.waiting || round.countdown) return;
    const h = hold.current;
    if (!matches(interval, round.targets[round.idx])) { h.since = 0; if (!timed) h.samples = []; return; }
    const now = performance.now();
    if (!h.since) h.since = now;
    if (now - h.since >= SETTLE_MS) h.samples.push(interval.cents);
    if (!timed && now - h.since >= HOLD_MS && h.samples.length) record(scoreOf(median(h.samples)));
  }, [interval]); // eslint-disable-line react-hooks/exhaustive-deps

  // Timed mode: each downbeat closes the current bar.
  useEffect(() => {
    if (!timed || !playing) return undefined;
    return clickBus.onBar(() => {
      const r = roundRef.current;
      if (!r || r.idx >= ROUND) return;
      if (r.countdown) { setRound({ ...r, countdown: r.countdown - 1 }); return; }
      if (r.bar + 1 < barsPer) { setRound({ ...r, bar: r.bar + 1 }); return; }
      const { samples } = hold.current;
      record(samples.length >= MIN_TIMED_SAMPLES ? scoreOf(median(samples)) : 0);
    });
  }, [timed, playing, barsPer]); // eslint-disable-line react-hooks/exhaustive-deps

  const [stoppedByClick, setStoppedByClick] = useState(false);
  useEffect(() => {
    if (!timed || !playing) return undefined;
    return clickBus.onStop(() => { setRound(null); setStoppedByClick(true); });
  }, [timed, playing]);
  useEffect(() => { if (round) setStoppedByClick(false); }, [round]);

  const weak = pool
    .map((t) => ({ ...t, avg: avgOf(history[keyOf(t)]) }))
    .filter((t) => t.avg !== null && t.avg < 70)
    .sort((a, b) => a.avg - b.avg)
    .slice(0, 3);

  const resetHistory = () => { setHistory({}); localStorage.removeItem(HISTORY_KEY); };

  return { round, playing, start, stop, skip: () => record(0), stoppedByClick, poolSize: pool.length, weak, resetHistory, hasHistory: Object.keys(history).length > 0 };
}
